import 'react-toastify/dist/ReactToastify.css';
import Delete from '@mui/icons-material/Delete';
import { CircularProgress } from "@mui/material";
import IconButton from '@mui/material/IconButton';
import axios from "axios";
import { t } from "i18next";
import React, { useRef, useState } from 'react';
import { toast } from "react-toastify";

import DatahubService from "../services/Datahub/DatahubService";
import toTitleCase from "../utils/toTitleCase";


const VehicleInfoForm = ({ vehicleList, setVehicleList, goToNextStep }) => {
    const [localVehicleList, setLocalVehicleList] = useState(vehicleList);
    const [registration, setRegistration] = useState('');
    const timer = useRef(null);
    const cancelToken = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleSearch = () => {
        setIsLoading(true);
        clearTimeout(timer.current);

        timer.current = setTimeout(() => {
            if (cancelToken.current) {
                cancelToken.current.cancel();
            }
            cancelToken.current = axios.CancelToken.source();

            const plate = registration;
            DatahubService.getDatahubVehicleIdentification({ plate }, cancelToken.current)
                .then(response => {
                    if (!response.data.vehicle) {
                        throw new Error();
                    }
                    response.data.vehicleID = new Date().getTime();
                    response.data.vehicle.plate = registration;
                    setLocalVehicleList((prevLocalVehicleList) => [...prevLocalVehicleList, response.data]);
            }).catch(() => {
                toast.warning(
                    t('vehicleInfoForm:dataRetrievalFailed'),
                    {
                        position: "top-center",
                        autoClose: 3500,
                        hideProgressBar: true,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    }
                );
            }).finally(() => {
                setRegistration('');
                setIsLoading(false);
            });
        }, 300);

        return () => {
            if (cancelToken.current) {
                cancelToken.current.cancel();
            }
        };
    };

    const handleDelete = (vehicleID) => {
        setLocalVehicleList((prevLocalVehicleList) => prevLocalVehicleList.filter(vehicle => vehicle.vehicleID !== vehicleID));
    };

    const handleVehiclePropertyChange = (vehicleID, propertyName, value) => {
        setLocalVehicleList((prevLocalVehicleList) => prevLocalVehicleList.map(vehicle => {
            if (vehicle.vehicleID === vehicleID) {
                return {
                    ...vehicle,
                    vehicle: {
                        ...vehicle.vehicle,
                        [propertyName]: value,
                    },
                };
            }
            return vehicle;
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setVehicleList(localVehicleList);
        goToNextStep();
    };

    return (
        <form
            onSubmit={handleSubmit}
            style={{
                background: '#e0e0e0',
                padding: '20px',
                borderRadius: '8px'
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    gap: '15px',
                    marginBottom: '15px'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '24px',
                    }}
                >
                    {isLoading && <CircularProgress size={24}/>}
                </div>
                <input
                    style={{
                        margin: '10px 0',
                        padding: '10px',
                        border: '1px solid #ddd'
                    }}
                    type="text"
                    placeholder={t('vehicleInfoForm:vehicleRegistration')}
                    value={registration}
                    onChange={(e) => setRegistration(e.target.value)}
                />
                <button
                    className="homepage-button"
                    style={{
                        width: '120px',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        margin: '10px',
                        borderRadius: '4px',
                    }}
                    type="button"
                    onClick={handleSearch}
                    disabled={registration.length === 0}
                >
                    {t('vehicleInfoForm:add')}
                </button>
            </div>
            {localVehicleList.map((vehicle) => (
                <div
                    key={vehicle.vehicleID}
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                    }}
                >
                    <div>
                        <p style={{ fontWeight: "bold" }}>{vehicle.vehicle.plate}</p>
                        <p>{`${t('vehicleInfoForm:brand')} : ${toTitleCase(vehicle.vehicle.make)}`}</p>
                        <p>{`${t('vehicleInfoForm:model')} : ${toTitleCase(vehicle.vehicle.model)}`}</p>
                        <p>{`${t('vehicleInfoForm:energy')} : ${toTitleCase(vehicle.vehicle.fuelTypeLabel)}`}</p>
                        <p>{`${t('vehicleInfoForm:category')} : ${toTitleCase(vehicle.vehicle.bodyTypeTransport)}`}</p>
                    </div>
                    <div
                        style={{
                            width: '150px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                            gap: '14px',
                        }}
                    >
                        <IconButton
                            style={{ cursor: 'pointer', marginBottom: '-10px', marginLeft: 'auto' }}
                            onClick={() => handleDelete(vehicle.vehicleID)}
                            aria-label="delete"
                        >
                            <Delete/>
                        </IconButton>
                        <label>
                            <input
                                type="checkbox"
                                checked={vehicle.vehicle.nonRolling}
                                onChange={(e) => handleVehiclePropertyChange(vehicle.vehicleID, "NonRolling", e.target.checked)}
                            />
                            {t('vehicleInfoForm:nonRunning')}
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={vehicle.vehicle.towable}
                                onChange={(e) => handleVehiclePropertyChange(vehicle.vehicleID, "Towable", e.target.checked)}
                            />
                            {t('vehicleInfoForm:towable')}
                        </label>
                    </div>
                </div>
            ))}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'end',
                }}
            >
                <button
                    className="homepage-button"
                    style={{
                        width: '120px',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                    }}
                    type="submit"
                    disabled={localVehicleList.length === 0}
                >
                    {t('common:next')}
                </button>
            </div>
        </form>
    );
}

export default VehicleInfoForm;
